/**
 * The document head for `/methodology`.
 *
 * `TechArticle` rather than `Article`, and that is the whole markup decision:
 * the page is a specification of a procedure, `TechArticle` is schema.org's
 * vocabulary for exactly that, and `proficiencyLevel` is a real field on it.
 * `HowTo` was the other candidate and is wrong — it describes steps a READER
 * performs, and nobody performs this; the machine does.
 *
 * No `Dataset`, despite the tables. A `Dataset` asserts a distributable body of
 * data with a licence and a distribution URL, and these are constants in a
 * source file rather than something anybody can download. Claiming otherwise
 * would be the one piece of dishonesty on the page whose entire argument is
 * that its numbers are what they say they are.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import {
  alternates,
  breadcrumbJsonLd,
  canonical,
  faqJsonLd,
  organizationJsonLd,
  socialMeta,
} from "../seo";
import { methodologyPath } from "./index";
import type { MethodologyCopy } from "./types";

export function methodologyHead(lang: ContentLang, copy: MethodologyCopy) {
  const path = methodologyPath(lang);
  return {
    meta: [
      { title: copy.title },
      { name: "description", content: copy.description },
      ...socialMeta({
        title: copy.title,
        description: copy.description,
        path,
        lang,
        imageAlt: copy.h1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: copy.h1,
          description: copy.description,
          url: canonical(path),
          inLanguage: lang,
          proficiencyLevel: "Expert",
          about: copy.h1,
        },
      },
      { "script:ld+json": faqJsonLd([...copy.faq]) },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: copy.h1, path },
        ]),
      },
      { "script:ld+json": organizationJsonLd() },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(methodologyPath),
    ],
  };
}
